import type { ColorParameter, ProjectConfigResponse, ThemeData } from './types';
import { apiClient } from '@/shared/api';

import themeConfigJson from '../../theme-config.json';

const localConfig = themeConfigJson as ProjectConfigResponse;

/**
 * Слияние параметров цвета по code: локальные — база, с бекенда перезаписывают по code.
 */
const mergeColorParametersByCode = (
  localParams: ColorParameter[],
  serverParams: ColorParameter[] | undefined,
): ColorParameter[] => {
  const byCode = new Map(localParams.map((p) => [p.code, { ...p }]));
  if (serverParams?.length) {
    for (const p of serverParams) {
      byCode.set(p.code, { ...p });
    }
  }
  return Array.from(byCode.values());
};

/**
 * Слияние одной темы по code: локальная — база, поля и colorParameter с бекенда перезаписывают.
 */
const mergeThemeByCode = (localTheme: ThemeData, serverTheme: ThemeData | undefined): ThemeData => {
  if (!serverTheme || serverTheme.code !== localTheme.code) {
    return localTheme;
  }
  return {
    id: serverTheme.id ?? localTheme.id,
    code: localTheme.code,
    name: serverTheme.name ?? localTheme.name,
    logoSvg: serverTheme.logoSvg ?? localTheme.logoSvg,
    colorParameter: mergeColorParametersByCode(localTheme.colorParameter, serverTheme.colorParameter),
  };
};

/**
 * Слияние конфигов по code: темы по theme.code, внутри темы colorParameter по param.code.
 * Локальный конфиг — база, данные с бекенда перезаписывают совпадающие по code.
 */
const mergeConfigsByCode = (
  local: ProjectConfigResponse,
  server: ProjectConfigResponse | null,
): ProjectConfigResponse => {
  if (!server?.themes?.length) {
    return local;
  }
  const localThemesByCode = new Map(local.themes.map((t) => [t.code, t]));
  const serverThemesByCode = new Map(server.themes.map((t) => [t.code, t]));

  const mergedThemes: ThemeData[] = local.themes.map((localTheme) => {
    const serverTheme = serverThemesByCode.get(localTheme.code);
    return mergeThemeByCode(localTheme, serverTheme);
  });

  // Добавляем темы с бекенда, которых нет в локальном (по code)
  for (const serverTheme of server.themes) {
    if (!localThemesByCode.has(serverTheme.code)) {
      mergedThemes.push(serverTheme);
    }
  }

  return {
    projectName: server.projectName ?? local.projectName,
    favicon: server.favicon ?? local.favicon,
    themes: mergedThemes,
  };
};

/**
 * Получение конфигурации проекта с backend с merge по code с локальным theme-config.json.
 * Темы сливаются по theme.code, параметры цвета — по colorParameter[].code.
 *
 * @returns Promise с конфигурацией (themes — массив ThemeData, colorParameter с code в snake_case)
 */
export const fetchThemeConfig = async (): Promise<ProjectConfigResponse> => {
  try {
    const { data } = await apiClient.get<ProjectConfigResponse>('/project-config');

    if (data?.themes && Array.isArray(data.themes) && data.themes.length > 0) {
      return mergeConfigsByCode(localConfig, data);
    }

    return localConfig;
  } catch (error) {
    console.warn('Failed to fetch theme config from server, using local fallback:', error);
    return localConfig;
  }
};
